import { motion, AnimatePresence, useInView, stagger, useAnimate } from 'framer-motion';
import { StackedContent, MotionImage, useSanitizedTranslation, TextBlock } from 'ui';
import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useWindowSize } from '@uidotdev/usehooks';

// Recommended for images used with this component to be square aspect ratio

export interface StickyScrollProps {
  title?: any;
  content: any,
  images: string [] | string;
  bg: string;
  flipped?: boolean;
  textbox?: boolean;
  alignText: string;
  mobileCenter?: boolean;
  sectionClass?: string;
  itemWrapperClass?: string;
}

export interface StickyItemsProps {
  item: any;
  trigger: any;
  state: number;
  index: number;
  image: string;
  textbox: boolean;
  alignText: string;
  mobileCenter?: boolean;
}

const StickyItems = ({ item, trigger, state, index, image, textbox, alignText, mobileCenter }: StickyItemsProps) => {
  const ref = useRef();
  const isInView = useInView(ref, { amount: 0, margin: '-40% 0% -40% 0%' });

  useEffect(() => {
    if (state !== index && isInView) {
      trigger(index);
    }
  }, [isInView, state, trigger]);


  return (
    <div ref={ref} className="relative z-10 flex flex-col items-center text-center lg:-translate-y-[24vh] xl:-translate-y-[21vh]">
      <motion.div id={'sticky' + index} className='flex flex-col items-center justify-center rounded-lg lg:rounded-none'>
        <div className='h-auto w-full lg:w-[75%] lg:p-5 block lg:hidden'>
          <Image
            src={image} 
            alt="icon"
            width={500} 
            height={500}
            priority={true}
            className='mx-auto'
          />
        </div>
        <div className={textbox ? 'bg-white px-16 py-28 rounded-xl' : ''}>
          <StackedContent 
            content={item} 
            center={ alignText === "center" ? true : false} 
            left={alignText != "left" ? false : true} 
            minHeight 
            mobileCenterLg={mobileCenter}
          />
        </div>
      </motion.div>
    </div>
  );
};

export const StickyScroll = ({ content, title, images, bg, flipped=false, textbox=false, alignText='left', mobileCenter=false, sectionClass="", itemWrapperClass=""}: StickyScrollProps) => {

    const [activeState, setState] = useState(0);

    return (
        <motion.section className={`w-full relative z-40 items-center ${sectionClass}`} data-testid={'text-section'} style={{background: bg ? bg : 'white'}}>
        {/* Title */}
        {title ?
          <div className='w-full flex items-center pt-20 lg:pt-32 pb-56 px-10'>
              <StackedContent 
                  full
                  center
                  content={title} 
              />
          </div>
          : <div className='lg:py-10'></div>
        }
        {/* Sticky Image and Text */}
        <div className="lg:mt-[100px] mx-24 lg:-mb-[150px] xl:-mb-[200px] 2xl:-mb-[240px]">
            <div className="mb-12 lg:my-32">
            <div className={`flex ${flipped ? 'flex-row-reverse' : 'flex-row'} gap-8`}>
                <div className={`flex items-start justify-center max-lg:hidden lg:basis-1/2`}>
                <div className="top-[calc(50vh)] -mt-4 flex w-full items-center justify-center lg:sticky">
                    <motion.div
                    className={`relative ${flipped ? 'lg:-right-[5%]' : 'lg:-left-[5%]'} lg:-translate-y-[50%] w-full aspect-video flex items-center justify-center`}
                    >
                        <div className="absolute -top-[11vh] w-full aspect-square">
                            <AnimatePresence>
                            {/* Recommended for images used with this component to be square aspect ratio */}
                            <MotionImage
                                fill
                                sizes="100vw"
                                className="h-auto w-full"
                                alt=""
                                key={'tvsticky' + activeState}
                                src={images[activeState]}
                                initial={{ opacity: 0 }}
                                animate={{ zIndex: 10, opacity: 1, transition: { ease: 'easeInOut', duration: 0.5 } }}
                                exit={{ opacity: 0, zIndex: 1, transition: { ease: 'easeInOut',duration: 0.1, delay: 0.1 } }}
                                priority={true}
                            />
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </div>
                </div>

                <div className={`relative z-10 flex w-full flex-col items-center text-center lg:basis-1/2 lg:space-y-[50vh] ${itemWrapperClass}`}>
                {content.map((item: any, index: number) => (
                    <StickyItems key={'tv-sticky-' + index} item={item} state={activeState} index={index} trigger={() => setState(index)} image={images[index]} textbox={textbox} alignText={alignText} mobileCenter={mobileCenter}/>
                ))}
                </div>
            </div>
            </div>
        </div>
        </motion.section>
    );
};